import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Loader2, Users, Check, Search } from "lucide-react";

const ROLE_SHORT = {
  admin: "Admin", dirigeant: "Direction", manager_commercial: "Manager",
  vendeur: "Vendeur", secretaire_bo: "Secrétaire", responsable_facturation: "Factu",
};

export default function MembersModal({ channelId, title, staff = [], lockedId, onClose, onSaved }) {
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get(`/chat/admin/members?channel_id=${encodeURIComponent(channelId)}`)
      .then((r) => setSelected(r.data.member_ids || []))
      .catch(() => setSelected([]))
      .finally(() => setLoading(false));
  }, [channelId]);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/chat/admin/members", { channel_id: channelId, member_ids: selected });
      toast.success("Accès mis à jour");
      onSaved && onSaved();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Mise à jour impossible"); setSaving(false); }
  };

  const list = staff.filter((u) =>
    u.id !== lockedId &&
    !["admin", "dirigeant"].includes(u.role) &&
    (u.name || "").toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" data-testid="members-modal">
      <div className="bg-white rounded-sm shadow-xl w-full max-w-md max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100">
          <h2 className="font-semibold text-[#111111] flex items-center gap-2 min-w-0">
            <Users className="w-4 h-4 text-[#B8873A] shrink-0" />
            <span className="truncate">Accès — {title}</span>
          </h2>
          <button onClick={onClose} data-testid="members-close" className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-5 pt-4">
          <p className="text-xs text-slate-500 mb-3">
            {lockedId ? "Ajoutez d'autres vendeurs à ce salon privé. Admin et Direction y ont toujours accès." : "Cochez les personnes qui ont accès à ce salon. Admin et Direction y ont toujours accès."}
          </p>
          <div className="flex items-center gap-2 border border-slate-200 rounded-sm px-2.5 py-1.5 mb-2">
            <Search className="w-3.5 h-3.5 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" data-testid="members-search"
              className="flex-1 text-sm focus:outline-none" />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 pb-2">
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-300" /></div>
          ) : list.length === 0 ? (
            <div className="text-sm text-slate-400 py-6 text-center">Aucune personne</div>
          ) : (
            list.map((u) => {
              const on = selected.includes(u.id);
              return (
                <button key={u.id} onClick={() => toggle(u.id)} data-testid={`member-toggle-${u.id}`}
                  className="w-full flex items-center gap-3 py-2 border-b border-slate-50 text-left">
                  <span className={`w-5 h-5 rounded-sm border flex items-center justify-center shrink-0 ${on ? "bg-[#111111] border-[#111111]" : "border-slate-300"}`}>
                    {on && <Check className="w-3.5 h-3.5 text-white" />}
                  </span>
                  <span className="flex-1 text-sm text-slate-700 truncate">{u.name}</span>
                  <span className="text-[10px] text-slate-400 shrink-0">{ROLE_SHORT[u.role] || u.role}</span>
                </button>
              );
            })
          )}
        </div>

        <div className="flex justify-end gap-2 px-5 py-3.5 border-t border-slate-100">
          <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-slate-600 hover:text-slate-900">Annuler</button>
          <button onClick={save} disabled={saving} data-testid="members-save"
            className="px-4 py-2 bg-[#111111] text-white rounded-sm text-sm font-semibold hover:bg-slate-800 disabled:opacity-50 flex items-center gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}
